import { useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft,
  Building2,
  MapPin,
  Loader2,
  ArrowRightCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  useCompany,
  useCompanyStats,
  useUpdateCompany,
} from '@/modules/core/companies/hooks/useCompanies';
import { COMPANY_TYPE_CONFIG } from '@/modules/core/companies/types/company.types';
import { CompanySmartButtons } from '../components/companies/CompanySmartButtons';
import { CompanyInfoTab } from '../components/companies/CompanyInfoTab';
import { CompanyOpportunitiesTab } from '../components/companies/CompanyOpportunitiesTab';

export function CompanyDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const companyId = id ? Number(id) : undefined;
  const initialTab = searchParams.get('tab') === 'opportunities' ? 'opportunities' : 'info';
  const [tab, setTab] = useState<'info' | 'opportunities'>(initialTab);
  const [convertOpen, setConvertOpen] = useState(false);

  const { data: company, isLoading, error } = useCompany(companyId);
  const { data: stats, isLoading: statsLoading } = useCompanyStats(companyId);
  const updateCompany = useUpdateCompany();

  const handleTabChange = (value: string) => {
    const next = value === 'opportunities' ? 'opportunities' : 'info';
    setTab(next);
    if (next === 'opportunities') {
      setSearchParams({ tab: 'opportunities' }, { replace: true });
    } else {
      setSearchParams({}, { replace: true });
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-copper" />
      </div>
    );
  }

  if (error || !company) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/tm/companies')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Retour à la liste
        </Button>
        <div className="p-4 bg-destructive/10 text-destructive rounded-lg">
          Société introuvable.
        </div>
      </div>
    );
  }

  const typeConfig = COMPANY_TYPE_CONFIG[company.companyType];

  const handleConvertToClient = () => {
    updateCompany.mutate(
      { id: company.id, data: { companyType: 'client' } },
      {
        onSuccess: () => {
          toast.success(`${company.name} passée en client`);
          setConvertOpen(false);
        },
        onError: () => toast.error('Erreur lors du changement de type'),
      }
    );
  };

  return (
    <div className="space-y-6">
      {/* Retour */}
      <Button
        variant="ghost"
        size="sm"
        asChild
        className="text-muted-foreground hover:text-foreground"
      >
        <Link to="/tm/companies">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Retour aux sociétés
        </Link>
      </Button>

      {/* En-tête */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div className="flex items-start gap-4 min-w-0 flex-1">
          <div className="p-3 bg-muted rounded-lg shrink-0">
            <Building2 className="h-8 w-8 text-muted-foreground" />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-2xl font-bold truncate">{company.name}</h1>
              <Badge className={`${typeConfig.bgColor} ${typeConfig.color}`}>
                {typeConfig.label}
              </Badge>
            </div>
            <div className="text-sm text-muted-foreground mt-1 space-y-0.5">
              {company.siret && (
                <div className="font-mono">SIRET: {company.siret}</div>
              )}
              {(company.city || company.postalCode) && (
                <div className="flex items-center gap-1">
                  <MapPin className="h-3.5 w-3.5" />
                  {company.postalCode ? `${company.postalCode} ` : ''}
                  {company.city}
                </div>
              )}
            </div>
          </div>
        </div>

        {company.companyType === 'prospect' && (
          <Button onClick={() => setConvertOpen(true)} variant="outline">
            <ArrowRightCircle className="h-4 w-4 mr-2" />
            Passer en client
          </Button>
        )}
      </div>

      {/* Smart buttons */}
      <CompanySmartButtons
        stats={stats}
        isLoading={statsLoading}
        onOpportunitiesClick={() => handleTabChange('opportunities')}
      />

      {/* Onglets */}
      <Tabs value={tab} onValueChange={handleTabChange}>
        <TabsList>
          <TabsTrigger value="info">Informations</TabsTrigger>
          <TabsTrigger value="opportunities">
            Opportunités
            {stats?.opportunitiesCount ? ` (${stats.opportunitiesCount})` : ''}
          </TabsTrigger>
        </TabsList>
        <TabsContent value="info" className="pt-6">
          <CompanyInfoTab company={company} />
        </TabsContent>
        <TabsContent value="opportunities" className="pt-6">
          <CompanyOpportunitiesTab companyId={company.id} />
        </TabsContent>
      </Tabs>

      {/* Dialog conversion */}
      <AlertDialog open={convertOpen} onOpenChange={setConvertOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Passer en client ?</AlertDialogTitle>
            <AlertDialogDescription>
              "{company.name}" sera marquée comme client.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleConvertToClient}>
              Confirmer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
